/* eslint-disable max-lines */
import {
  BankAccount,
  BlockchainNetwork,
  FinancialInstitutionName,
  WALLET_TYPES,
  STABLECOIN,
  CURRENCY,
} from '~/types'
import { BlockchainAddress } from '~/app/store/blockchainAddresses/types'

export type TemporaryHoldingBalance = {
  totalBalance: string
  withdrawDailyRemaining: string
}

export type DepositRequest = {
  amount: string
  bank_account: {
    account_no: string
    bank_name: string
    bank_abbrev: string
  }
  created_at: string
  expired_at: string
}

export type DashboardBanner = {
  title: string
  tag: string
  content: string
}

export type FinancialInstitution = {
  id: number
  name: FinancialInstitutionName
  enabled: boolean
}

export type StablecoinFee = {
  amount: number | undefined
  identifier: string | undefined
  timestamp: string | undefined
}

export type PersonalVerificationStatus = 'initial' | 'pending' | 'verified' | 'rejected'
export type FeeType = 'PercentageFeeInstruction' | 'FixedFeeInstruction'

export type FiatFee = {
  amount: number | undefined
  feeType: FeeType | undefined
  rate: number | undefined
  maxAmount: number | undefined
  minAmount: number | undefined
}

export enum TRANSFER_METHOD {
  // TODO(edwincapel): remove any type when merged (STRAITSX-20144/refactor-STABLECOIN-is-already-declared)
  // eslint-disable-next-line @typescript-eslint/no-shadow
  STABLECOIN = 'stablecoin',
  BANK = 'bank',
}
export type BankTransferNetwork = BANK_TRANSFER_NETWORKS | null

export type WithdrawWalletIdType =
  | WALLET_TYPES.DIGITAL_GOODS
  | WALLET_TYPES.TEMPORARY_HOLDING_BALANCE
  | undefined

export enum BANK_TRANSFER_NETWORKS {
  LOCAL = 'local',
  FAST = 'fast',
  SWIFT = 'swift',
}

export type WithdrawLimits = {
  [key in STABLECOIN]: {
    FIAT_MIN: number
    FIAT_MAX: number
    STABLECOIN_MAX: number
    STABLECOIN_MIN: number
  }
}

export enum LANDING_PAGE_ACTIONS {
  SET_PERSONAL_ACCOUNT_MAP = 'LANDING_PAGE_ACTIONS.SET_PERSONAL_ACCOUNT_MAP',
  SET_TEMP_HOLDING_BALANCE = 'LANDING_PAGE_ACTIONS.SET_TEMP_HOLDING_BALANCE',
  SET_LATEST_PERSONAL_DEPOSIT_REQUESTS = 'LANDING_PAGE_ACTIONS.SET_LATEST_PERSONAL_DEPOSIT_REQUESTS',
  SET_IS_ZRC2_DEPOSIT_ENABLED = 'LANDING_PAGE_ACTIONS.SET_IS_ZRC2_DEPOSIT_ENABLED',
  SET_IS_ERC20_DEPOSIT_ENABLED = 'LANDING_PAGE_ACTIONS.SET_IS_ERC20_DEPOSIT_ENABLED',
  SET_IS_HTS_DEPOSIT_ENABLED = 'LANDING_PAGE_ACTIONS.SET_IS_HTS_DEPOSIT_ENABLED',
  SET_IS_MATIC_DEPOSIT_ENABLED = 'LANDING_PAGE_ACTIONS.SET_IS_MATIC_DEPOSIT_ENABLED',
  SET_IS_AVAX_DEPOSIT_ENABLED = 'LANDING_PAGE_ACTIONS.SET_IS_AVAX_DEPOSIT_ENABLED',
  SET_IS_TRC20_DEPOSIT_ENABLED = 'LANDING_PAGE_ACTIONS.SET_IS_TRC20_DEPOSIT_ENABLED',
  SET_IS_BEP20_DEPOSIT_ENABLED = 'LANDING_PAGE_ACTIONS.SET_IS_BEP20_DEPOSIT_ENABLED',
  SET_IS_ARB_DEPOSIT_ENABLED = 'LANDING_PAGE_ACTIONS.SET_IS_ARB_DEPOSIT_ENABLED',
  SET_IS_XRP_DEPOSIT_ENABLED = 'LANDING_PAGE_ACTIONS.SET_IS_XRP_DEPOSIT_ENABLED',
  SET_IS_ZRC2_WITHDRAWAL_ENABLED = 'LANDING_PAGE_ACTIONS.SET_IS_ZRC2_WITHDRAWAL_ENABLED',
  SET_IS_ERC20_WITHDRAWAL_ENABLED = 'LANDING_PAGE_ACTIONS.SET_IS_ERC20_WITHDRAWAL_ENABLED',
  SET_IS_HTS_WITHDRAWAL_ENABLED = 'LANDING_PAGE_ACTIONS.SET_IS_HTS_WITHDRAWAL_ENABLED',
  SET_IS_MATIC_WITHDRAWAL_ENABLED = 'LANDING_PAGE_ACTIONS.SET_IS_MATIC_WITHDRAWAL_ENABLED',
  SET_IS_AVAX_WITHDRAWAL_ENABLED = 'LANDING_PAGE_ACTIONS.SET_IS_AVAX_WITHDRAWAL_ENABLED',
  SET_IS_TRC20_WITHDRAWAL_ENABLED = 'LANDING_PAGE_ACTIONS.SET_IS_TRC20_WITHDRAWAL_ENABLED',
  SET_IS_BEP20_WITHDRAWAL_ENABLED = 'LANDING_PAGE_ACTIONS.SET_IS_BEP20_WITHDRAWAL_ENABLED',
  SET_IS_ARB_WITHDRAWAL_ENABLED = 'LANDING_PAGE_ACTIONS.SET_IS_ARB_WITHDRAWAL_ENABLED',
  SET_IS_XRP_WITHDRAWAL_ENABLED = 'LANDING_PAGE_ACTIONS.SET_IS_XRP_WITHDRAWAL_ENABLED',
  SET_IS_DASHBOARD_BANNER_MANAGEMENT_ENABLED = 'LANDING_PAGE_ACTIONS.SET_IS_DASHBOARD_BANNER_MANAGEMENT_ENABLED',
  SET_IS_ZRC2_NOTICE_BANNER_VISIBLE = 'LANDING_PAGE_ACTIONS.SET_IS_ZRC2_NOTICE_BANNER_VISIBLE',
  SET_DASHBOARD_BANNERS = 'ROUTE_ACTIONS.SET_DASHBOARD_BANNERS',
  SET_TAB = 'LANDING_PAGE_ACTIONS.SET_TAB',
  SET_TRANSFER_METHOD = 'LANDING_PAGE_ACTIONS.SET_TRANSFER_METHOD',
  SET_BANK_TRANSFER_NETWORK = 'LANDING_PAGE_ACTIONS.SET_BANK_TRANSFER_NETWORK',
  SET_SELECTED_CURRENCY = 'LANDING_PAGE_ACTIONS.SET_SELECTED_CURRENCY',
  SET_SELECTED_BLOCKCHAIN = 'LANDING_PAGE_ACTIONS.SET_SELECTED_BLOCKCHAIN',
  SET_SELECTED_BANK_ACCOUNT = 'LANDING_PAGE_ACTIONS.SET_SELECTED_BANK_ACCOUNT',
  SET_SELECTED_BLOCKCHAIN_ADDRESS = 'LANDING_PAGE_ACTIONS.SET_SELECTED_BLOCKCHAIN_ADDRESS',
  SET_WITHDRAW_AMOUNT = 'LANDING_PAGE_ACTIONS.SET_WITHDRAW_AMOUNT',
  SET_IS_STABLECOIN_FEE_LOADING = 'LANDING_PAGE_ACTIONS.SET_IS_STABLECOIN_FEE_LOADING',
  SET_STABLECOIN_FEE = 'LANDING_PAGE_ACTIONS.SET_STABLECOIN_FEE',
  SET_FIAT_FEE = 'LANDING_PAGE_ACTIONS.SET_FIAT_FEE',
  SET_IS_HTS_WITHDRAWAL_FEE_CHARGING = 'LANDING_PAGE_ACTIONS.SET_IS_HTS_WITHDRAWAL_FEE_CHARGING',
  SET_CONFIRM_MODAL_OPEN = 'LANDING_PAGE_ACTIONS.SET_CONFIRM_MODAL_OPEN',
  SET_2_FA_MODAL_OPEN = 'LANDING_PAGE_ACTIONS.SET_2_FA_MODAL_OPEN',
  CREATE_WITHDRAWAL_REQUEST = 'LANDING_PAGE_ACTIONS.CREATE_WITHDRAWAL_REQUEST',
  SET_TEMP_HOLDING_BALANCE_MODAL_OPEN = 'LANDING_PAGE_ACTIONS.SET_TEMP_HOLDING_BALANCE_MODAL_OPEN',
  SET_WITHDRAW_WALLET_ID = 'LANDING_PAGE_ACTIONS.SET_WITHDRAW_WALLET_ID',
  SET_VISITED_GETTING_STARTED = 'LANDING_PAGE_ACTIONS.SET_VISITED_GETTING_STARTED',
  SET_IS_HEDERA_UI_ENABLED = 'LANDING_PAGE_ACTIONS.SET_IS_HEDERA_UI_ENABLED',
  SET_IS_POLYGON_UI_ENABLED = 'LANDING_PAGE_ACTIONS.SET_IS_POLYGON_UI_ENABLED',
  SET_IS_AVALANCHE_UI_ENABLED = 'LANDING_PAGE_ACTIONS.SET_IS_AVALANCHE_UI_ENABLED',
  SET_IS_TRON_UI_ENABLED = 'LANDING_PAGE_ACTIONS.SET_IS_TRON_UI_ENABLED',
  SET_IS_BNBSMART_UI_ENABLED = 'LANDING_PAGE_ACTIONS.SET_IS_BNBSMART_UI_ENABLED',
  SET_IS_ARBITRUM_UI_ENABLED = 'LANDING_PAGE_ACTIONS.SET_IS_ARBITRUM_UI_ENABLED',
  SET_IS_RIPPLE_UI_ENABLED = 'LANDING_PAGE_ACTIONS.SET_IS_RIPPLE_UI_ENABLED',
  SET_IS_TEMPORARY_HOLDING_BALANCE_WITHDRAWAL = 'LANDING_PAGE_ACTIONS.SET_IS_TEMPORARY_HOLDING_BALANCE_WITHDRAWAL',
  SET_IS_PERIODIC_REVIEW_REQUIRED = 'LANDING_PAGE_ACTIONS.SET_IS_PERIODIC_REVIEW_REQUIRED ',
  SET_HAS_FETCHING_FEE_ERROR = 'LANDING_PAGE_ACTIONS.SET_HAS_FETCHING_FEE_ERROR ',
  SET_IS_DPT_APPLIED = 'LANDING_PAGE_ACTIONS.SET_IS_DPT_APPLIED',
  SET_IS_SSO_TWO_FA_AUTHENTICATOR_ENABLED = 'BLOCKCHAIN_ADDRESS_ACTIONS.SET_IS_SSO_TWO_FA_AUTHENTICATOR_ENABLED',
  SET_IS_DIRECT_MINT_LIMIT_REMOVAL_ENABLED = 'LANDING_PAGE_ACTIONS.SET_IS_DIRECT_MINT_LIMIT_REMOVAL_ENABLED',
  SET_IS_DPT_RISK_MODAL_ENABLED = 'LANDING_PAGE_ACTIONS.SET_IS_DPT_RISK_MODAL_ENABLED',
  SET_IS_FAZZ_VA_FOR_DASHBOARD_ENABLED = 'LANDING_PAGE_ACTIONS.SET_IS_FAZZ_VA_FOR_DASHBOARD_ENABLED',
  SET_IS_FAZZ_VA_FOR_DIRECT_MINT_ENABLED = 'LANDING_PAGE_ACTIONS.SET_IS_FAZZ_VA_FOR_DIRECT_MINT_ENABLED',
  SET_IS_TRAVEL_RULE_DEPOSIT_DECLARATION_ENABLED = 'LANDING_PAGE_ACTIONS.SET_IS_TRAVEL_RULE_DEPOSIT_DECLARATION_ENABLED',
  SET_IS_TRAVEL_RULE_ADDR_WHITELISTING_ENABLED = 'LANDING_PAGE_ACTIONS.SET_IS_TRAVEL_RULE_ADDR_WHITELISTING_ENABLED',
  SET_IS_RENEW_ADDRESS_ACKNOWLEDGED = 'LANDING_PAGE_ACTIONS.SET_IS_RENEW_ADDRESS_ACKNOWLEDGED',
  SET_IS_XUSD_COACHMARK_ACTIVE = 'LANDING_PAGE_ACTIONS.SET_IS_XUSD_COACHMARK_ACTIVE',
  SET_IS_DORMANT_BANNER_COACHMARK_ACTIVE = 'LANDING_PAGE_ACTIONS.SET_IS_DORMANT_BANNER_COACHMARK_ACTIVE',
  SET_IS_USDC_MATIC_DEPOSIT_ENABLED = 'LANDING_PAGE_ACTIONS.SET_IS_USDC_MATIC_DEPOSIT_ENABLED',
  SET_IS_USDC_MATIC_WITHDRAWAL_ENABLED = 'LANDING_PAGE_ACTIONS.SET_IS_USDC_MATIC_WITHDRAWAL_ENABLED',
  SET_IS_USDT_BEP20_DEPOSIT_ENABLED = 'LANDING_PAGE_ACTIONS.SET_IS_USDT_BEP20_DEPOSIT_ENABLED',
  SET_IS_USDT_BEP20_WITHDRAWAL_ENABLED = 'LANDING_PAGE_ACTIONS.SET_IS_USDT_BEP20_WITHDRAWAL_ENABLED',
  SET_IS_USDT_BEP20_UI_ENABLED = 'LANDING_PAGE_ACTIONS.SET_IS_USDT_BEP20_UI_ENABLED',
  SET_ACTIVATION_CHECKLIST_CLICKED_ELEMENT = 'LANDING_PAGE_ACTIONS.SET_ACTIVATION_CHECKLIST_CLICKED_ELEMENT',
  SET_IS_CKA_ENABLED = 'LANDING_PAGE_ACTIONS.SET_IS_CKA_ENABLED',
  SET_IS_CKA_MASK_VISIBLE = 'LANDING_PAGE_ACTIONS.SET_IS_CKA_MASK_VISIBLE',
  SET_IS_XUSD_ERC20_DEPOSIT_ENABLED = 'LANDING_PAGE_ACTIONS.SET_IS_XUSD_ERC20_DEPOSIT_ENABLED',
  SET_IS_XUSD_ERC20_WITHDRAWAL_ENABLED = 'LANDING_PAGE_ACTIONS.SET_IS_XUSD_ERC20_WITHDRAWAL_ENABLED',
}

type SetIsXsgdZrc2DepositEnabled = {
  type: typeof LANDING_PAGE_ACTIONS.SET_IS_ZRC2_DEPOSIT_ENABLED
  isXsgdZrc2DepositEnabled: boolean
}

type SetIsXsgdErc20DepositEnabled = {
  type: typeof LANDING_PAGE_ACTIONS.SET_IS_ERC20_DEPOSIT_ENABLED
  isXsgdErc20DepositEnabled: boolean
}

type SetIsXsgdHtsDepositEnabled = {
  type: typeof LANDING_PAGE_ACTIONS.SET_IS_HTS_DEPOSIT_ENABLED
  isXsgdHtsDepositEnabled: boolean
}

type SetIsXsgdMaticDepositEnabled = {
  type: typeof LANDING_PAGE_ACTIONS.SET_IS_MATIC_DEPOSIT_ENABLED
  isXsgdMaticDepositEnabled: boolean
}

type SetIsXsgdAvaxDepositEnabled = {
  type: typeof LANDING_PAGE_ACTIONS.SET_IS_AVAX_DEPOSIT_ENABLED
  isXsgdAvaxDepositEnabled: boolean
}

type SetIsXsgdArbDepositEnabled = {
  type: typeof LANDING_PAGE_ACTIONS.SET_IS_ARB_DEPOSIT_ENABLED
  isXsgdArbDepositEnabled: boolean
}

type SetIsXsgdXrpDepositEnabled = {
  type: typeof LANDING_PAGE_ACTIONS.SET_IS_XRP_DEPOSIT_ENABLED
  isXsgdXrpDepositEnabled: boolean
}

type SetIsUsdtTrc20DepositEnabled = {
  type: typeof LANDING_PAGE_ACTIONS.SET_IS_TRC20_DEPOSIT_ENABLED
  isUsdtTrc20DepositEnabled: boolean
}

type SetIsXusdBep20DepositEnabled = {
  type: typeof LANDING_PAGE_ACTIONS.SET_IS_BEP20_DEPOSIT_ENABLED
  isXusdBep20DepositEnabled: boolean
}

type SetIsXusdErc20DepositEnabled = {
  type: typeof LANDING_PAGE_ACTIONS.SET_IS_XUSD_ERC20_DEPOSIT_ENABLED
  isXusdErc20DepositEnabled: boolean
}

type SetIsXsgdZrc2WithdrawalEnabled = {
  type: typeof LANDING_PAGE_ACTIONS.SET_IS_ZRC2_WITHDRAWAL_ENABLED
  isXsgdZrc2WithdrawalEnabled: boolean
}

type SetIsXsgdErc20WithdrawalEnabled = {
  type: typeof LANDING_PAGE_ACTIONS.SET_IS_ERC20_WITHDRAWAL_ENABLED
  isXsgdErc20WithdrawalEnabled: boolean
}

type SetIsXsgdHtsWithdrawalEnabled = {
  type: typeof LANDING_PAGE_ACTIONS.SET_IS_HTS_WITHDRAWAL_ENABLED
  isXsgdHtsWithdrawalEnabled: boolean
}

type SetIsXsgdMaticWithdrawalEnabled = {
  type: typeof LANDING_PAGE_ACTIONS.SET_IS_MATIC_WITHDRAWAL_ENABLED
  isXsgdMaticWithdrawalEnabled: boolean
}

type SetIsXsgdAvaxWithdrawalEnabled = {
  type: typeof LANDING_PAGE_ACTIONS.SET_IS_AVAX_WITHDRAWAL_ENABLED
  isXsgdAvaxWithdrawalEnabled: boolean
}

type SetIsXsgdArbWithdrawalEnabled = {
  type: typeof LANDING_PAGE_ACTIONS.SET_IS_ARB_WITHDRAWAL_ENABLED
  isXsgdArbWithdrawalEnabled: boolean
}

type SetIsXsgdXrpWithdrawalEnabled = {
  type: typeof LANDING_PAGE_ACTIONS.SET_IS_XRP_WITHDRAWAL_ENABLED
  isXsgdXrpWithdrawalEnabled: boolean
}

type SetIsUsdtTrc20WithdrawalEnabled = {
  type: typeof LANDING_PAGE_ACTIONS.SET_IS_TRC20_WITHDRAWAL_ENABLED
  isUsdtTrc20WithdrawalEnabled: boolean
}

type SetIsXusdErc20WithdrawalEnabled = {
  type: typeof LANDING_PAGE_ACTIONS.SET_IS_XUSD_ERC20_WITHDRAWAL_ENABLED
  isXusdErc20WithdrawalEnabled: boolean
}

type SetIsXusdBep20WithdrawalEnabled = {
  type: typeof LANDING_PAGE_ACTIONS.SET_IS_BEP20_WITHDRAWAL_ENABLED
  isXusdBep20WithdrawalEnabled: boolean
}

// USDC MATIC
type SetIsUsdcMaticDepositEnabled = {
  type: typeof LANDING_PAGE_ACTIONS.SET_IS_USDC_MATIC_DEPOSIT_ENABLED
  isUsdcMaticDepositEnabled: boolean
}
type SetIsUsdcMaticWithdrawalEnabled = {
  type: typeof LANDING_PAGE_ACTIONS.SET_IS_USDC_MATIC_WITHDRAWAL_ENABLED
  isUsdcMaticWithdrawalEnabled: boolean
}

type SetIsUsdtBep20DepositEnabled = {
  type: typeof LANDING_PAGE_ACTIONS.SET_IS_USDT_BEP20_DEPOSIT_ENABLED
  isUsdtBep20DepositEnabled: boolean
}
type SetIsUsdtBep20WithdrawalEnabled = {
  type: typeof LANDING_PAGE_ACTIONS.SET_IS_USDT_BEP20_WITHDRAWAL_ENABLED
  isUsdtBep20WithdrawalEnabled: boolean
}

type setIsUsdtBep20UIEnabled = {
  type: typeof LANDING_PAGE_ACTIONS.SET_IS_USDT_BEP20_UI_ENABLED
  isUSDTBEP20UIEnabled: boolean
}

type SetTempHoldingBalance = {
  type: typeof LANDING_PAGE_ACTIONS.SET_TEMP_HOLDING_BALANCE
  temporaryHoldingBalance: TemporaryHoldingBalance | null
}

type SetLatestPersonalDepositRequests = {
  type: typeof LANDING_PAGE_ACTIONS.SET_LATEST_PERSONAL_DEPOSIT_REQUESTS
  latestPersonalDepositRequests: DepositRequest[]
}

type SetIsZrc2NoticeBannerEnabled = {
  type: typeof LANDING_PAGE_ACTIONS.SET_IS_ZRC2_NOTICE_BANNER_VISIBLE
  isZrc2NoticeBannerVisible: boolean
}

type SetIsDashboardBannerManagementEnabled = {
  type: typeof LANDING_PAGE_ACTIONS.SET_IS_DASHBOARD_BANNER_MANAGEMENT_ENABLED
  isDashboardBannerManagementEnabled: boolean
}

type SetDashboardBanners = {
  type: typeof LANDING_PAGE_ACTIONS.SET_DASHBOARD_BANNERS
  dashboardBanners: DashboardBanner[]
}

type SetTab = {
  type: typeof LANDING_PAGE_ACTIONS.SET_TAB
  tab: string
}

type SetTransferMethod = {
  type: typeof LANDING_PAGE_ACTIONS.SET_TRANSFER_METHOD
  transferMethod: TRANSFER_METHOD | null
}

type SetBankTransferNetwork = {
  type: typeof LANDING_PAGE_ACTIONS.SET_BANK_TRANSFER_NETWORK
  bankTransferNetwork: BankTransferNetwork | null
}

type SetSelectedCurrency = {
  type: typeof LANDING_PAGE_ACTIONS.SET_SELECTED_CURRENCY
  selectedCurrency: STABLECOIN | null
}

type SetSelectedBlockchain = {
  type: typeof LANDING_PAGE_ACTIONS.SET_SELECTED_BLOCKCHAIN
  selectedBlockchain: BlockchainNetwork | null
}

type SetSelectedBankAccount = {
  type: typeof LANDING_PAGE_ACTIONS.SET_SELECTED_BANK_ACCOUNT
  selectedBankAccount: BankAccount | null
}

type SetSelectedBlockchainAddress = {
  type: typeof LANDING_PAGE_ACTIONS.SET_SELECTED_BLOCKCHAIN_ADDRESS
  selectedBlockchainAddress: BlockchainAddress | null
}

type SetWithdrawAmount = {
  type: typeof LANDING_PAGE_ACTIONS.SET_WITHDRAW_AMOUNT
  amount: string
  withdrawAmountError: string
}

type SetWithdrawWalletId = {
  type: typeof LANDING_PAGE_ACTIONS.SET_WITHDRAW_WALLET_ID
  withdrawWalletId: WithdrawWalletIdType
}

type SetIsStablecoinFeeLoading = {
  type: typeof LANDING_PAGE_ACTIONS.SET_IS_STABLECOIN_FEE_LOADING
  isStablecoinFeeLoading: boolean
}

type SetStablecoinFee = {
  type: typeof LANDING_PAGE_ACTIONS.SET_STABLECOIN_FEE
  amount: number
  identifier: string
  timestamp: string
}

type SetFiatFee = {
  type: typeof LANDING_PAGE_ACTIONS.SET_FIAT_FEE
  stablecoin: CURRENCY
  amount: number
  feeType: FeeType
  rate: number
  maxAmount: number
  minAmount: number
}

type SetIsHtsWithdrawalFeeCharging = {
  type: typeof LANDING_PAGE_ACTIONS.SET_IS_HTS_WITHDRAWAL_FEE_CHARGING
  isHtsWithdrawalFeeCharging: boolean
}

type SetConfirmModalOpen = {
  type: typeof LANDING_PAGE_ACTIONS.SET_CONFIRM_MODAL_OPEN
  open: boolean
}

type Set2FaModalOpen = {
  type: typeof LANDING_PAGE_ACTIONS.SET_2_FA_MODAL_OPEN
  open: boolean
}

type CreateWithdrawalRequest = {
  type: typeof LANDING_PAGE_ACTIONS.CREATE_WITHDRAWAL_REQUEST
  error: string
}

type SetTempHoldingBalanceModalOpen = {
  type: typeof LANDING_PAGE_ACTIONS.SET_TEMP_HOLDING_BALANCE_MODAL_OPEN
  open: boolean
}

type SetVisitedGettingStarted = {
  type: typeof LANDING_PAGE_ACTIONS.SET_VISITED_GETTING_STARTED
  value: boolean
}

type SetIsHederaUIEnabled = {
  type: typeof LANDING_PAGE_ACTIONS.SET_IS_HEDERA_UI_ENABLED
  isHederaUIEnabled: boolean
}

type SetIsAvalancheUIEnabled = {
  type: typeof LANDING_PAGE_ACTIONS.SET_IS_AVALANCHE_UI_ENABLED
  isAvalancheUIEnabled: boolean
}

type SetIsTronUIEnabled = {
  type: typeof LANDING_PAGE_ACTIONS.SET_IS_TRON_UI_ENABLED
  isTronUIEnabled: boolean
}

type SetIsBnbsmartUIEnabled = {
  type: typeof LANDING_PAGE_ACTIONS.SET_IS_BNBSMART_UI_ENABLED
  isBnbsmartUIEnabled: boolean
}

type SetIsPolygonUIEnabled = {
  type: typeof LANDING_PAGE_ACTIONS.SET_IS_POLYGON_UI_ENABLED
  isPolygonUIEnabled: boolean
}

type SetIsArbitrumUIEnabled = {
  type: typeof LANDING_PAGE_ACTIONS.SET_IS_ARBITRUM_UI_ENABLED
  isArbitrumUIEnabled: boolean
}

type SetIsRippleUIEnabled = {
  type: typeof LANDING_PAGE_ACTIONS.SET_IS_RIPPLE_UI_ENABLED
  isRippleUIEnabled: boolean
}

type SetIsTemporaryHoldingBalanceWithdrawal = {
  type: typeof LANDING_PAGE_ACTIONS.SET_IS_TEMPORARY_HOLDING_BALANCE_WITHDRAWAL
  isTemporaryHoldingBalanceWithdrawal: boolean
}

type SetIsPeriodicReviewRequired = {
  type: typeof LANDING_PAGE_ACTIONS.SET_IS_PERIODIC_REVIEW_REQUIRED
  isPeriodicReviewRequired: boolean
}

type SetHasFetchingFeeError = {
  type: typeof LANDING_PAGE_ACTIONS.SET_HAS_FETCHING_FEE_ERROR
  hasError: boolean
}

type SetIsDptApplied = {
  type: typeof LANDING_PAGE_ACTIONS.SET_IS_DPT_APPLIED
  isDptApplied: boolean
}

type SetIsSsoTwoFaAuthenticatorEnabled = {
  type: typeof LANDING_PAGE_ACTIONS.SET_IS_SSO_TWO_FA_AUTHENTICATOR_ENABLED
  isSsoTwoFaAuthenticatorEnabled: boolean
}

type SetIsDptRiskModalApplied = {
  type: typeof LANDING_PAGE_ACTIONS.SET_IS_DPT_RISK_MODAL_ENABLED
  isDptRiskModalEnabled: boolean
}

type SetIsFazzVaForDashboardEnabled = {
  type: typeof LANDING_PAGE_ACTIONS.SET_IS_FAZZ_VA_FOR_DASHBOARD_ENABLED
  isFazzVaForDashboardEnabled: boolean
}

type SetIsFazzVaForDirectMintEnabled = {
  type: typeof LANDING_PAGE_ACTIONS.SET_IS_FAZZ_VA_FOR_DIRECT_MINT_ENABLED
  isFazzVaForDirectMintEnabled: boolean
}

type SetIsTravelRuleDepositDeclarationEnabled = {
  type: typeof LANDING_PAGE_ACTIONS.SET_IS_TRAVEL_RULE_DEPOSIT_DECLARATION_ENABLED
  isTravelRuleDepositDeclarationEnabled: boolean
}

type SetIsTravelRuleAddrWhitelistingEnabled = {
  type: typeof LANDING_PAGE_ACTIONS.SET_IS_TRAVEL_RULE_ADDR_WHITELISTING_ENABLED
  isTravelRuleAddrWhitelistingEnabled: boolean
}

type SetIsRenewAddressAcknowledged = {
  type: typeof LANDING_PAGE_ACTIONS.SET_IS_RENEW_ADDRESS_ACKNOWLEDGED
  isRenewAddressAcknowledged: boolean
}

type SetIsXusdCoachMarkActive = {
  type: typeof LANDING_PAGE_ACTIONS.SET_IS_XUSD_COACHMARK_ACTIVE
  isXusdCoachmarkActive: boolean
}

type SetIsDormantBannerCoachmarkActive = {
  type: typeof LANDING_PAGE_ACTIONS.SET_IS_DORMANT_BANNER_COACHMARK_ACTIVE
  isDormantBannerCoachmarkActive: boolean
}

type SetActivationChecklistClickedElement = {
  type: typeof LANDING_PAGE_ACTIONS.SET_ACTIVATION_CHECKLIST_CLICKED_ELEMENT
  activationChecklistClickedElement: string
}

type SetIsCkaEnabled = {
  type: typeof LANDING_PAGE_ACTIONS.SET_IS_CKA_ENABLED
  isCkaEnabled: boolean
}

type SetIsCkaMaskVisible = {
  type: typeof LANDING_PAGE_ACTIONS.SET_IS_CKA_MASK_VISIBLE
  isCkaMaskVisible: boolean
}

export type ActionTypes =
  | SetTempHoldingBalance
  | SetLatestPersonalDepositRequests
  | SetIsXsgdZrc2DepositEnabled
  | SetIsXsgdErc20DepositEnabled
  | SetIsXsgdHtsDepositEnabled
  | SetIsXsgdMaticDepositEnabled
  | SetIsXsgdAvaxDepositEnabled
  | SetIsXsgdArbDepositEnabled
  | SetIsXsgdXrpDepositEnabled
  | SetIsUsdtTrc20DepositEnabled
  | SetIsXusdBep20DepositEnabled
  | SetIsXusdErc20DepositEnabled
  | SetIsXsgdZrc2WithdrawalEnabled
  | SetIsXsgdErc20WithdrawalEnabled
  | SetIsXsgdHtsWithdrawalEnabled
  | SetIsXsgdMaticWithdrawalEnabled
  | SetIsXsgdAvaxWithdrawalEnabled
  | SetIsXsgdArbWithdrawalEnabled
  | SetIsXsgdXrpWithdrawalEnabled
  | SetIsUsdtTrc20WithdrawalEnabled
  | SetIsXusdErc20WithdrawalEnabled
  | SetIsXusdBep20WithdrawalEnabled
  | SetIsUsdcMaticDepositEnabled
  | SetIsUsdcMaticWithdrawalEnabled
  | SetIsUsdtBep20DepositEnabled
  | SetIsUsdtBep20WithdrawalEnabled
  | setIsUsdtBep20UIEnabled
  | SetIsZrc2NoticeBannerEnabled
  | SetIsDashboardBannerManagementEnabled
  | SetDashboardBanners
  | SetTab
  | SetTransferMethod
  | SetBankTransferNetwork
  | SetSelectedCurrency
  | SetSelectedBlockchain
  | SetSelectedBankAccount
  | SetSelectedBlockchainAddress
  | SetWithdrawAmount
  | SetWithdrawWalletId
  | SetIsStablecoinFeeLoading
  | SetStablecoinFee
  | SetFiatFee
  | SetIsHtsWithdrawalFeeCharging
  | SetConfirmModalOpen
  | Set2FaModalOpen
  | CreateWithdrawalRequest
  | SetTempHoldingBalanceModalOpen
  | SetVisitedGettingStarted
  | SetIsHederaUIEnabled
  | SetIsPolygonUIEnabled
  | SetIsAvalancheUIEnabled
  | SetIsTronUIEnabled
  | SetIsBnbsmartUIEnabled
  | SetIsArbitrumUIEnabled
  | SetIsRippleUIEnabled
  | SetIsTemporaryHoldingBalanceWithdrawal
  | SetIsPeriodicReviewRequired
  | SetHasFetchingFeeError
  | SetIsDptApplied
  | SetIsSsoTwoFaAuthenticatorEnabled
  | SetIsDptRiskModalApplied
  | SetIsFazzVaForDashboardEnabled
  | SetIsFazzVaForDirectMintEnabled
  | SetIsTravelRuleDepositDeclarationEnabled
  | SetIsTravelRuleAddrWhitelistingEnabled
  | SetIsRenewAddressAcknowledged
  | SetIsXusdCoachMarkActive
  | SetIsDormantBannerCoachmarkActive
  | SetActivationChecklistClickedElement
  | SetIsCkaEnabled
  | SetIsCkaMaskVisible
